import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  calendarioSemanaMesServiceByDate,
  calendarioSemanaMesServiceById,
} from '../services/calendario-semana-mes.service'
import type { CalendarWeekMonth } from '../types/calendar-week-month.type'
import type { DemonstrativoMetaFamiliaParams } from './use-demonstrativo-meta-familia'

export type CalendarWeekMonthByDate = {
  date: string
}

export type CalendarWeekMonthById = {
  id: number
}

export type FetchCalendarWeekMonthPayload =
  | CalendarWeekMonthByDate
  | CalendarWeekMonthById

type NavigateCalendarWeekDirection = 'previous' | 'next'

export function useCalendarWeekMonth() {
  const [calendarWeekMonth, setCalendarWeekMonth] = useState<
    CalendarWeekMonth[]
  >([])
  const [isLoadingCalendarWeekMonth, setIsLoadingCalendarWeekMonth] =
    useState(false)
  const [selectedSemanaIdParams, setSelectedSemanaIdParams] =
    useState<DemonstrativoMetaFamiliaParams>({
      idSemana: 0,
      iniDate: '',
      endDate: '',
    })

  const selectedCalendarWeek = calendarWeekMonth[0]

  const form = useForm<CalendarWeekMonthByDate>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
    },
  })

  const handleFetchCalendarWeekMonth = async (
    data: FetchCalendarWeekMonthPayload,
  ) => {
    try {
      setIsLoadingCalendarWeekMonth(true)

      const { body } =
        'id' in data
          ? await calendarioSemanaMesServiceById({ id: data.id })
          : await calendarioSemanaMesServiceByDate({
              date: dayjs(data.date).utc().startOf('day').format('YYYY-MM-DD'),
            })

      let calendario: CalendarWeekMonth | CalendarWeekMonth[]

      try {
        calendario = JSON.parse(body)
      } catch (error) {
        console.error('Erro ao fazer parse do JSON:', error)
        return
      }

      const calendarWeeks = Array.isArray(calendario)
        ? calendario
        : [calendario]

      if (calendarWeeks.length === 0) {
        return
      }

      setCalendarWeekMonth(calendarWeeks)
      setSelectedSemanaIdParams({
        idSemana: calendarWeeks[0].idCalendarioSemana,
        iniDate: calendarWeeks[0].dataInicioSemana,
        endDate: calendarWeeks[0].dataFimSemana,
      })
    } catch (error) {
      console.error('Erro ao buscar calendário semana/mês:', error)
    } finally {
      setIsLoadingCalendarWeekMonth(false)
    }
  }

  const handleNavigateCalendarWeek = (
    direction: NavigateCalendarWeekDirection,
  ) => {
    if (!selectedCalendarWeek || isLoadingCalendarWeekMonth) {
      return
    }

    handleFetchCalendarWeekMonth({
      id:
        selectedCalendarWeek.idCalendarioSemana +
        (direction === 'next' ? 1 : -1),
    })
  }

  useEffect(() => {
    handleFetchCalendarWeekMonth(form.getValues())
  }, [])

  return {
    calendarWeekMonth,
    form,
    handleFetchCalendarWeekMonth,
    handleNavigateCalendarWeek,
    isLoadingCalendarWeekMonth,
    selectedCalendarWeek,
    selectedSemanaIdParams,
  }
}
